import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Logo from '../components/Logo';
import LangToggle from '../components/LangToggle';
import { useAuth } from '../lib/auth';
import { ApiError } from '../lib/api';

// The landing page. Two-column on wide screens — atmospheric editorial hero
// on the left, cream login card on the right. Stacks on small screens with
// the login card on top for mobile-first ergonomics.
export default function Landing() {
    const { t } = useTranslation();
    const { signIn } = useAuth();
    const navigate = useNavigate();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        setError(null); setSubmitting(true);
        try {
            await signIn(email.trim(), password);
            navigate('/app');
        } catch (err) {
            if (err instanceof ApiError) {
                if (err.code === 'BadCredentials')      setError(t('auth.errors.badCredentials'));
                else if (err.code === 'Locked')         setError(t('auth.errors.locked'));
                else if (err.code === 'NoActiveContract') setError(t('auth.errors.noActiveContract'));
                else if (err.code === 'AccountSuspended') setError(t('auth.errors.suspended'));
                else setError(err.message || t('auth.errors.unknown'));
            } else {
                setError(t('auth.errors.unknown'));
            }
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="relative min-h-screen overflow-hidden bg-slate-base">
            {/* Mesh accent — settles in the top-right; the grain is on body. */}
            <div aria-hidden="true" className="pointer-events-none fixed inset-0 bg-mesh-ember" />

            {/* Top bar — thin, brand mark on the left, lang toggle on the right */}
            <header className="relative z-10 flex items-center justify-between px-6 sm:px-12 py-6">
                <Logo size="md" />
                <LangToggle />
            </header>

            <div className="relative z-10 grid lg:grid-cols-12 gap-10 lg:gap-16 px-6 sm:px-12 pb-16 pt-6">
                {/* -------- Hero column -------- */}
                <section className="order-2 lg:order-1 lg:col-span-7 max-w-[680px] lg:py-16 stagger">
                    <p className="eyebrow text-ember">
                        {t('brand.tagline')}
                    </p>

                    <div className="rule-ember w-12 my-4" />

                    <h1 className="font-display text-ink-on
                                  text-[clamp(2.5rem,5vw,4.5rem)] leading-[0.95] tracking-tight text-balance"
                        style={{ fontVariationSettings: '"opsz" 144, "SOFT" 60' }}>
                        {t('auth.landing.title')}
                    </h1>

                    <p className="mt-6 font-body text-lg leading-relaxed text-ink-on/85 max-w-[560px] text-balance">
                        {t('auth.landing.sub')}
                    </p>

                    {/* Editorial detail rows — three faint horizontal rows that
                        echo the table aesthetic used elsewhere in the app. */}
                    <div className="mt-12 grid gap-0 max-w-[520px] border-t border-slate-line">
                        <FeatureRow num="01" label={t('nav.statement')}
                                    body="Full ledger for every unit you own — debits, credits, balance." />
                        <FeatureRow num="02" label={t('nav.upload')}
                                    body="Bank-transfer receipts and proof of payment, sent to staff in one step." />
                        <FeatureRow num="03" label={t('nav.profile')}
                                    body="Manage your contact details, ID images, and language preference." />
                    </div>

                    <p className="mt-12 text-[11px] uppercase tracking-widest2 text-ink-dim">
                        {t('auth.landing.secure')}
                    </p>
                </section>

                {/* -------- Login card -------- */}
                {/* order-1 on mobile so the sign-in card sits at the top, above the fold;
                    restored to the right column on lg. */}
                <section className="order-1 lg:order-2 lg:col-span-5 self-start lg:self-center lg:pt-2 lg:py-16 animate-fade-up">
                    <div className="card-paper relative overflow-hidden">
                        {/* a single ember corner mark */}
                        <span aria-hidden="true" className="absolute top-0 left-0 w-12 h-px bg-ember" />
                        <span aria-hidden="true" className="absolute top-0 left-0 w-px h-12 bg-ember" />

                        <div className="mb-8">
                            <p className="eyebrow text-ember mb-2">{t('common.signIn')}</p>
                            <h2 className="font-display text-3xl text-ink leading-tight tracking-tight">
                                {t('common.signIn')}
                            </h2>
                        </div>

                        <form onSubmit={onSubmit} className="space-y-6" noValidate>
                            <PaperField
                                label={t('auth.emailLabel')}
                                type="email"
                                autoComplete="username"
                                value={email}
                                onChange={(v) => setEmail(v)}
                                required
                            />
                            <PaperField
                                label={t('auth.passwordLabel')}
                                type="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={(v) => setPassword(v)}
                                required
                            />

                            {error && (
                                <div role="alert" className="text-sm text-ember-deep border-l-2 border-ember-deep pl-3 py-1">
                                    {error}
                                </div>
                            )}

                            <div className="flex items-center justify-between pt-2">
                                <Link to="/forgot" className="text-sm text-ink/70 hover:text-ember underline underline-offset-4 decoration-1">
                                    {t('auth.forgotPassword')}
                                </Link>
                                <button
                                    type="submit"
                                    className="btn btn-primary min-w-[140px]"
                                    disabled={submitting}
                                >
                                    {submitting ? t('auth.signingIn') : t('auth.signInCta')}
                                </button>
                            </div>
                        </form>
                    </div>
                </section>
            </div>

            <footer className="relative z-10 px-6 sm:px-12 pb-8 text-center">
                <p className="text-[11px] uppercase tracking-widest2 text-ink-dim">
                    {t('brand.full')}
                </p>
            </footer>
        </div>
    );
}

// --- Small subcomponents used only on this page -------------------------

function FeatureRow({ num, label, body }: { num: string; label: string; body: string }) {
    return (
        <div className="flex items-baseline gap-6 py-5 border-b border-slate-line/60">
            <span className="font-display text-ember tabular-nums text-lg">{num}</span>
            <div className="min-w-0">
                <div className="text-ink-on font-body font-medium">{label}</div>
                <div className="text-ink-muted text-sm mt-0.5 leading-relaxed">{body}</div>
            </div>
        </div>
    );
}

// "Paper field" — input rendered on the cream card. Underline-only style,
// no border-box; that's what gives it the editorial-paper character.
interface PaperFieldProps {
    label:        string;
    type:         'email' | 'password' | 'text';
    value:        string;
    onChange:     (v: string) => void;
    autoComplete?: string;
    required?:     boolean;
}

function PaperField({ label, type, value, onChange, autoComplete, required }: PaperFieldProps) {
    return (
        <label className="block">
            <span className="block text-[10px] uppercase tracking-widest2 text-ink/60 mb-2 font-medium">
                {label}
            </span>
            <input
                type={type}
                value={value}
                required={required}
                autoComplete={autoComplete}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-transparent border-0 border-b border-cream-line/80 px-0 py-2
                           text-ink font-body text-base
                           focus:outline-none focus:border-ember
                           transition-colors placeholder:text-ink/40"
            />
        </label>
    );
}
